import { writable, derived, get } from 'svelte/store'

// Types for text layers
export type TextLayer = 'original' | 'current' | 'corrected' | 'preview'

export interface TextLayers {
  original: string    // Original input text
  current: string     // Currently editing text
  corrected: string   // Text with corrections applied
  preview: string     // Preview for mini-editor
  
  // Metadata
  lastModified: {
    original: Date | null
    current: Date | null
    corrected: Date | null
    preview: Date | null
  }
  
  // History tracking for undo/redo
  history: string[]
  historyIndex: number
}

export interface TextStatistics {
  characters: number
  words: number
  lines: number
  lastModified: Date | null
}

// Constants
const MAX_HISTORY_SIZE = 100

// Initialize store
const initialState: TextLayers = {
  original: '',
  current: '',
  corrected: '',
  preview: '',
  lastModified: {
    original: new Date(),
    current: new Date(),
    corrected: null,
    preview: null
  },
  history: [],
  historyIndex: -1
}

// Create the main store
export const textLayersStore = writable<TextLayers>(initialState)

// Derived stores for specific layers
export const originalTextStore = derived(
  textLayersStore,
  $layers => $layers.original
)

export const currentTextStore = derived(
  textLayersStore,
  $layers => $layers.current
)

export const correctedTextStore = derived(
  textLayersStore,
  $layers => $layers.corrected
)

export const previewTextStore = derived(
  textLayersStore,
  $layers => $layers.preview
)

// Derived store for undo/redo availability
export const historyStatusStore = derived(
  textLayersStore,
  $layers => ({
    canUndo: $layers.historyIndex >= 0,
    canRedo: $layers.historyIndex < $layers.history.length - 1,
    historySize: $layers.history.length
  })
)

// Action functions
export const textLayersActions = {
  // Set text for each layer
  setOriginal(text: string) {
    textLayersStore.update(layers => ({
      ...layers,
      original: text,
      current: text, // Auto-sync to current when original changes
      lastModified: {
        ...layers.lastModified,
        original: new Date(),
        current: new Date()
      }
    }))
  },
  
  setCurrent(text: string, addToHistory = true) {
    textLayersStore.update(layers => {
      let newHistory = layers.history
      let newHistoryIndex = layers.historyIndex
      
      if (addToHistory) {
        // Clear future history if we're not at the end
        if (layers.historyIndex < layers.history.length - 1) {
          newHistory = layers.history.slice(0, layers.historyIndex + 1)
        }
        
        // Add to history
        newHistory = [...newHistory, text]
        
        // Limit history size
        if (newHistory.length > MAX_HISTORY_SIZE) {
          newHistory = newHistory.slice(-MAX_HISTORY_SIZE)
        }
        
        newHistoryIndex = newHistory.length - 1
      }
      
      return {
        ...layers,
        current: text,
        lastModified: {
          ...layers.lastModified,
          current: new Date()
        },
        history: newHistory,
        historyIndex: newHistoryIndex
      }
    })
  },
  
  setCorrected(text: string) {
    textLayersStore.update(layers => ({
      ...layers,
      corrected: text,
      lastModified: {
        ...layers.lastModified,
        corrected: new Date()
      }
    }))
  },
  
  setPreview(text: string) {
    textLayersStore.update(layers => ({
      ...layers,
      preview: text,
      lastModified: {
        ...layers.lastModified,
        preview: new Date()
      }
    }))
  },
  
  // Layer operations
  syncLayers(from: TextLayer, to: TextLayer) {
    textLayersStore.update(layers => {
      const text = layers[from]
      return {
        ...layers,
        [to]: text,
        lastModified: {
          ...layers.lastModified,
          [to]: new Date()
        }
      }
    })
  },
  
  clearLayer(layer: TextLayer) {
    textLayersStore.update(layers => ({
      ...layers,
      [layer]: '',
      lastModified: {
        ...layers.lastModified,
        [layer]: null
      }
    }))
  },
  
  // History management
  undo() {
    textLayersStore.update(layers => {
      if (layers.historyIndex < 0) return layers
      
      const newIndex = layers.historyIndex - 1
      const text = newIndex >= 0 ? layers.history[newIndex] : ''
      
      return {
        ...layers,
        current: text,
        historyIndex: newIndex,
        lastModified: {
          ...layers.lastModified,
          current: new Date()
        }
      }
    })
  },
  
  redo() {
    textLayersStore.update(layers => {
      if (layers.historyIndex >= layers.history.length - 1) return layers
      
      const newIndex = layers.historyIndex + 1
      const text = layers.history[newIndex]
      
      return {
        ...layers,
        current: text,
        historyIndex: newIndex,
        lastModified: {
          ...layers.lastModified,
          current: new Date()
        }
      }
    })
  },
  
  clearHistory() {
    textLayersStore.update(layers => ({
      ...layers,
      history: [],
      historyIndex: -1
    }))
  },
  
  // Batch operations
  applyCorrection() {
    textLayersStore.update(layers => ({
      ...layers,
      current: layers.corrected,
      lastModified: {
        ...layers.lastModified,
        current: new Date()
      },
      history: [...layers.history, layers.corrected],
      historyIndex: layers.history.length
    }))
  },
  
  rejectCorrection() {
    textLayersStore.update(layers => ({
      ...layers,
      corrected: '',
      lastModified: {
        ...layers.lastModified,
        corrected: null
      }
    }))
  },
  
  reset() {
    textLayersStore.set({
      ...initialState,
      lastModified: {
        original: new Date(),
        current: new Date(),
        corrected: null,
        preview: null
      }
    })
  },
  
  // Comparison utilities
  hasDifferences(layer1: TextLayer, layer2: TextLayer): boolean {
    const layers = get(textLayersStore)
    return layers[layer1] !== layers[layer2]
  },
  
  getStatistics(layer: TextLayer): TextStatistics {
    const layers = get(textLayersStore)
    const text = layers[layer]
    
    const characters = text.length
    const words = text.split(/\s+/).filter(w => w.length > 0).length
    const lines = text.split('\n').length
    
    return {
      characters,
      words,
      lines,
      lastModified: layers.lastModified[layer]
    }
  }
}

// Helper functions
export function getActiveText(): string {
  const layers = get(textLayersStore)
  return layers.current || layers.original
}

export function getTextForLayer(layer: TextLayer): string {
  const layers = get(textLayersStore)
  return layers[layer]
}

// Subscribe to all layers for debugging
export function subscribeToTextLayers(callback: (layers: TextLayers) => void) {
  return textLayersStore.subscribe(callback)
}

// Export function to get current state
export function getTextLayersState(): TextLayers {
  return get(textLayersStore)
}